import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HeartPulse, Loader2, RefreshCw, AlertTriangle, CheckCircle2, UploadCloud, ExternalLink, Pencil, History, ShieldCheck, Database, Cloud } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { api, fileUrl } from '../../lib/api';
import { formatDate } from '../../components/Shared';

const STATUS = {
  durable: { label: 'OK', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  local_only: { label: 'Local only (not durable)', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  missing: { label: 'MISSING', cls: 'bg-red-50 text-red-800 border-red-200' },
  deleted: { label: 'Deleted', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  external: { label: 'External link', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const Stat = ({ label, value, tone = 'navy', testid }) => (
  <Card data-testid={testid}><CardContent className="p-4">
    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`text-2xl font-black mt-1 ${tone === 'red' ? 'text-red-700' : tone === 'amber' ? 'text-amber-700' : tone === 'green' ? 'text-emerald-700' : 'text-[#1a2744]'}`}>{value}</p>
  </CardContent></Card>
);

// Upload a replacement under the EXACT missing filename: every page that
// references it heals at once, no article editing needed.
const ReplaceButton = ({ filename, onDone }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const ext = (filename.split('.').pop() || '').toLowerCase();
  const accept = ext === 'jpg' || ext === 'jpeg' ? '.jpg,.jpeg' : `.${ext}`;
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post(`/admin/media/replace/${encodeURIComponent(filename)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Restored ${filename} (${(r.data.heals || []).length} place${(r.data.heals || []).length === 1 ? '' : 's'} healed)`);
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Replacement failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={pick} data-testid={`replace-input-${filename}`} />
      <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} className="h-8 text-xs font-bold" data-testid={`replace-btn-${filename}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UploadCloud className="h-3.5 w-3.5 mr-1" />} Upload replacement ({accept})
      </Button>
    </>
  );
};

export default function AdminImageHealth() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [integrity, setIntegrity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mirroring, setMirroring] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, hist, integ] = await Promise.all([
        api.get('/admin/media/health'), api.get('/admin/media/health/history'), api.get('/admin/media/integrity'),
      ]);
      setData(h.data); setHistory(hist.data || []); setIntegrity(integ.data);
    } catch (e) {
      toast.error('Could not run the health check');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const mirrorNow = async () => {
    setMirroring(true);
    try {
      const r = await api.post('/admin/media/mirror-now');
      toast.success(`Mirror complete: ${r.data.copied} file(s) copied to the second store`);
      await load();
    } catch (e) {
      toast.error('Mirror failed');
    } finally {
      setMirroring(false);
    }
  };

  const run = data?.run;
  const articles = data?.articles || [];
  const problems = articles.filter((a) => a.problem_count > 0 || (a.status === 'published' && !a.has_featured_image));
  const shown = showAll ? articles : problems;
  const siteMissing = integrity?.missing || [];
  const otherMissing = siteMissing.filter((r) => !r.usages.some((u) => u.collection === 'news_posts'));

  return (
    <div className="space-y-6" data-testid="admin-image-health-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-black text-[#1a2744] flex items-center gap-2"><HeartPulse className="h-6 w-6 text-[#C9A84C]" /> Image Health Check</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">Scans every Newsroom article (published and draft) and every page that references an uploaded file, then reports anything that cannot be served. Missing files can be repaired here by uploading a replacement under the same filename, which fixes every page that uses it.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" onClick={load} disabled={loading} className="h-10 font-bold" data-testid="health-rescan">{loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Scan again</Button>
          <Button onClick={mirrorNow} disabled={mirroring} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="health-mirror-now">{mirroring ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />} Mirror all files now</Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center gap-2 text-slate-600 py-10"><Loader2 className="h-5 w-5 animate-spin" /> Checking every article and file...</div>
      ) : run && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Articles scanned" value={run.articles_total} testid="health-stat-articles" />
            <Stat label="Articles with problems" value={run.articles_with_problems} tone={run.articles_with_problems ? 'red' : 'green'} testid="health-stat-problems" />
            <Stat label="Images missing" value={run.images_missing} tone={run.images_missing ? 'red' : 'green'} testid="health-stat-missing" />
            <Stat label="Published without an image" value={run.published_without_image} tone={run.published_without_image ? 'amber' : 'navy'} testid="health-stat-noimage" />
            <Stat label="Missing files site-wide" value={run.site_missing_total} tone={run.site_missing_total ? 'red' : 'green'} testid="health-stat-site" />
          </div>

          <Card className={run.cloud_reachable ? 'border-emerald-200' : 'border-amber-300'} data-testid="health-storage-status"><CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
            <div className="flex items-center gap-2"><Cloud className={`h-4 w-4 ${run.cloud_reachable ? 'text-emerald-600' : 'text-amber-600'}`} /> Cloud store: <strong>{run.cloud_reachable ? 'reachable' : 'not reachable right now'}</strong></div>
            <div className="flex items-center gap-2"><Database className="h-4 w-4 text-emerald-600" /> Database store: <strong>active</strong></div>
            {integrity && <div className="text-slate-600">Referenced files: {integrity.referenced_total} · in two stores: {integrity.rows.filter((r) => r.copies >= 2).length} · single store: {integrity.single_copy?.length || 0}</div>}
            <div className="sm:ml-auto text-xs text-slate-500">Checked {formatDate(run.checked_at)} by {run.actor}</div>
          </CardContent></Card>

          {problems.length === 0 && otherMissing.length === 0 && (
            <Card className="border-emerald-200 bg-emerald-50/60" data-testid="health-all-clear"><CardContent className="p-5 flex items-center gap-3 text-emerald-900">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
              <div><p className="font-bold">Every Newsroom image is present and every referenced file resolves.</p><p className="text-sm">New uploads are stored in two independent places automatically.</p></div>
            </CardContent></Card>
          )}

          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-serif font-bold text-lg text-[#1a2744]">Newsroom articles {showAll ? `(all ${articles.length})` : `needing attention (${problems.length})`}</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAll((v) => !v)} className="text-xs font-bold" data-testid="health-toggle-all">{showAll ? 'Show problems only' : 'Show all articles'}</Button>
            </div>
            <div className="space-y-3" data-testid="health-article-list">
              {shown.length === 0 && <p className="text-sm text-slate-500">Nothing to show.</p>}
              {shown.map((a) => (
                <Card key={a.id} className={a.problem_count ? 'border-red-200' : ''} data-testid={`health-article-${a.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className={a.status === 'published' ? 'border-emerald-300 text-emerald-800' : 'border-slate-300 text-slate-600'}>{a.status}</Badge>
                          {a.featured_missing && <Badge className="bg-red-600 text-white">Featured image missing</Badge>}
                          {!a.has_featured_image && a.status === 'published' && <Badge variant="outline" className="border-amber-300 text-amber-800">No featured image</Badge>}
                        </div>
                        <p className="font-serif font-bold text-[#1a2744] mt-1 leading-snug">{a.title}</p>
                        <p className="text-xs text-slate-500">{a.category} · {a.published_at ? formatDate(a.published_at) : 'not published'}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold"><Link to={a.edit_url} data-testid={`health-edit-${a.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit article</Link></Button>
                        {a.status === 'published' && <Button asChild size="sm" variant="ghost" className="h-8 text-xs font-bold"><a href={a.public_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1" /> View</a></Button>}
                      </div>
                    </div>
                    {a.images.length > 0 && (
                      <ul className="mt-3 divide-y divide-slate-100 border border-slate-100 rounded-lg">
                        {a.images.map((im) => {
                          const st = STATUS[im.status] || STATUS.missing;
                          return (
                            <li key={im.role + im.filename + im.url} className="p-2.5 flex flex-col sm:flex-row sm:items-center gap-2 text-sm" data-testid={`health-image-${im.filename || 'ext'}`}>
                              {im.status === 'durable' || im.status === 'local_only' ? (
                                <img src={fileUrl(im.url)} alt="" className="h-10 w-14 object-cover rounded border border-slate-200 shrink-0" />
                              ) : (
                                <div className="h-10 w-14 rounded border border-dashed border-red-300 bg-red-50 flex items-center justify-center shrink-0"><AlertTriangle className="h-4 w-4 text-red-600" /></div>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="font-mono text-xs text-slate-700 truncate">{im.filename || im.url}</p>
                                <p className="text-[11px] text-slate-500">{im.role === 'featured' ? 'Featured image' : 'Inside the article'}{im.original_name ? ` · originally "${im.original_name}"` : ''}{im.uploaded_at ? ` · uploaded ${formatDate(im.uploaded_at)}` : ''}{im.status === 'durable' ? ` · ${im.copies} durable cop${im.copies === 1 ? 'y' : 'ies'}` : ''}</p>
                              </div>
                              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                              {(im.status === 'missing' || im.status === 'deleted') && im.filename && <ReplaceButton filename={im.filename} onDone={load} />}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {otherMissing.length > 0 && (
            <section data-testid="health-other-missing">
              <h2 className="font-serif font-bold text-lg text-[#1a2744] mb-2">Missing files on other pages ({otherMissing.length})</h2>
              <div className="space-y-2">
                {otherMissing.map((r) => (
                  <Card key={r.filename} className="border-red-200"><CardContent className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-xs text-slate-700 truncate">{r.filename}{r.original_name ? ` (originally "${r.original_name}")` : ''}</p>
                      <p className="text-[11px] text-slate-500">Used by: {r.usages.map((u) => `${u.label}${u.name ? `: ${u.name}` : ''}`).join(' · ')}</p>
                    </div>
                    <ReplaceButton filename={r.filename} onDone={load} />
                  </CardContent></Card>
                ))}
              </div>
            </section>
          )}

          {history.length > 0 && (
            <section data-testid="health-history">
              <h2 className="font-serif font-bold text-lg text-[#1a2744] mb-2 flex items-center gap-2"><History className="h-4 w-4" /> Recent checks</h2>
              <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-2">When</th><th className="p-2">By</th><th className="p-2">Articles with problems</th><th className="p-2">Images missing</th><th className="p-2">Site-wide missing</th><th className="p-2">Cloud</th></tr></thead>
                  <tbody>
                    {history.slice(0, 12).map((h) => (
                      <tr key={h.id} className="border-t border-slate-100">
                        <td className="p-2 whitespace-nowrap">{new Date(h.checked_at).toLocaleString()}</td>
                        <td className="p-2">{h.actor}</td>
                        <td className={`p-2 font-bold ${h.articles_with_problems ? 'text-red-700' : 'text-emerald-700'}`}>{h.articles_with_problems}</td>
                        <td className={`p-2 font-bold ${h.images_missing ? 'text-red-700' : 'text-emerald-700'}`}>{h.images_missing}</td>
                        <td className={`p-2 font-bold ${h.site_missing_total ? 'text-red-700' : 'text-emerald-700'}`}>{h.site_missing_total}</td>
                        <td className="p-2">{h.cloud_reachable ? 'ok' : 'down'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
